import { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { db } from '@/lib/db';
import type { GroupRoundEnriched, Member } from '@/lib/db/types';
import { defaultStakeForFormat, showWolfPointMultipliers } from '@/lib/games/engine/stakes-helpers';
import { DEFAULT_WOLF_ROUND_CONFIG, mergeWolfConfig } from '@/lib/games/formats/wolf';
import type { StakesConfig, WolfRoundConfig, NassauMatchConfig } from '@/lib/games/engine/types';
import { WolfSetupPanel } from './WolfSetupPanel';
import { playerKeyFor } from '@/lib/db/group-rounds';
import { CategorizedGamePicker, defaultEnabledGames } from './CategorizedGamePicker';
import { StakesScoringPanel, buildInitialStakes } from './StakesScoringPanel';
import { autoTeams, enabledGamesNeedTeams, stakesToGameRows } from './useRoundEngine';
import { buildTeamsFromSetup, RoundTeamsSetupPanel } from './RoundTeamsSetupPanel';
import { Sheet } from '@/components/shared/Sheet';

type Step = 'pick' | 'stakes' | 'teams';

interface Props {
  round: GroupRoundEnriched;
  member: Member;
  onClose: () => void;
  onSaved: () => void;
}

export function AddGamesFlow({ round, member, onClose, onSaved }: Props) {
  const existingIds = useMemo(
    () => new Set((round.games || []).filter((g) => g.enabled).map((g) => g.format_id)),
    [round.games],
  );

  const [step, setStep] = useState<Step>('pick');
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => {
    const base = defaultEnabledGames();
    Object.keys(base).forEach((k) => { base[k] = false; });
    existingIds.forEach((id) => { base[id] = true; });
    return base;
  });
  const [stakes, setStakes] = useState<StakesConfig>(() => buildInitialStakes([...existingIds]));
  const [wolfConfig, setWolfConfig] = useState<WolfRoundConfig>(() => {
    const g = round.games?.find((x) => x.enabled && (x.format_id === 'wolf' || x.format_id === 'lone-wolf'));
    return mergeWolfConfig((g?.config as { wolfConfig?: WolfRoundConfig })?.wolfConfig);
  });
  const [teamMode, setTeamMode] = useState<'auto' | 'manual'>('auto');
  const [manualTeams, setManualTeams] = useState<Record<string, number>>({});
  const [matchOpponents, setMatchOpponents] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    (round.games || []).forEach((g) => {
      if (g.enabled && g.format_id === 'match-play') {
        const opp = (g.config as { opponentKey?: string })?.opponentKey;
        if (opp) initial[g.format_id] = opp;
      }
    });
    return initial;
  });
  const [nassauMatches, setNassauMatches] = useState<Record<string, NassauMatchConfig[]>>(() => {
    const initial: Record<string, NassauMatchConfig[]> = {};
    (round.games || []).forEach((g) => {
      if (g.enabled && (g.format_id === 'nassau' || g.format_id === 'team-nassau')) {
        const matches = (g.config as { matches?: NassauMatchConfig[] })?.matches;
        if (matches?.length) initial[g.format_id] = matches;
      }
    });
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const players = round.players || [];
  const playerCount = players.length;
  const roster = useMemo(() => players.map((p) => ({
    id: p.member_id || p.player_key,
    name: p.display_name + (p.member_id === member.id ? ' (you)' : ''),
    player_key: p.player_key,
  })), [players, member.id]);

  const orgKey = playerKeyFor(member.id);
  const stakesRoster = useMemo(() => roster.map((p) => ({
    player_key: p.player_key,
    name: p.name.replace(' (you)', ''),
    isOrganizer: p.name.includes('(you)'),
  })), [roster]);

  const enabledIds = useMemo(
    () => Object.entries(enabled).filter(([, v]) => v).map(([k]) => k),
    [enabled],
  );

  const newIds = enabledIds.filter((id) => !existingIds.has(id));
  const hasChanges = newIds.length > 0 || [...existingIds].some((id) => !enabled[id]);
  const needsTeams = enabledGamesNeedTeams(enabledIds);

  const previewTeams = useMemo(() => {
    const keys = players.map((p) => p.player_key);
    return buildTeamsFromSetup(roster, keys, teamMode, manualTeams, autoTeams);
  }, [players, roster, teamMode, manualTeams]);

  const wolfStakeConfig = stakes.wolf ?? stakes['lone-wolf'] ?? defaultStakeForFormat('wolf');
  const wolfShowMult = showWolfPointMultipliers(wolfStakeConfig);

  const toggleGame = (id: string) => {
    setEnabled((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (!prev[id]) {
        setStakes((s) => ({ ...s, ...buildInitialStakes([id]) }));
      }
      return next;
    });
  };

  const persistGames = async (idsToSave: string[]) => {
    const keys = players.map((p) => p.player_key);
    const orgKey = playerKeyFor(member.id);

    let teams = round.config?.teams as string[][] | undefined;
    if (needsTeams) {
      teams = buildTeamsFromSetup(roster, keys, teamMode, manualTeams, autoTeams);
    }

    const gameMode = idsToSave.length ? 'games' : 'casual';
    await db.groupRounds.update(round.id, {
      config: { ...round.config, teams, game_mode: gameMode },
    });

    const keptRows = (round.games || [])
      .filter((g) => g.enabled && idsToSave.includes(g.format_id) && !newIds.includes(g.format_id))
      .map((g) => ({
        format_id: g.format_id,
        enabled: true,
        stakes: g.stakes as Record<string, unknown>,
        config: g.config as Record<string, unknown>,
      }));

    const newRows = stakesToGameRows(
      newIds,
      stakes,
      players.map((p) => ({ player_key: p.player_key, member_id: p.member_id })),
      orgKey,
      enabledIds.includes('wolf') || enabledIds.includes('lone-wolf') ? wolfConfig : undefined,
      matchOpponents,
      nassauMatches,
      teams,
    );

    const keptWithWolf = keptRows.map((row) => {
      if (row.format_id === 'wolf' || row.format_id === 'lone-wolf') {
        return { ...row, config: { ...row.config, wolfConfig } };
      }
      if (row.format_id === 'nassau' || row.format_id === 'team-nassau') {
        const matches = nassauMatches[row.format_id];
        if (matches?.length) {
          return { ...row, config: { ...row.config, matches } };
        }
      }
      return row;
    });

    await db.groupRounds.setGames(round.id, [...keptWithWolf, ...newRows]);
    onSaved();
  };

  const save = async () => {
    if (!hasChanges) return;
    setSubmitting(true);
    setError(null);
    try {
      await persistGames(enabledIds);
    } catch (err) {
      setError((err as Error).message || 'Could not save games');
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (step === 'pick') {
      if (!hasChanges) {
        setError('Change at least one game — add new ones or tap to remove');
        return;
      }
      if (!enabledIds.length) {
        setError(null);
        void save();
        return;
      }
      setError(null);
      if (newIds.length) setStep('stakes');
      else if (needsTeams) setStep('teams');
      else void save();
      return;
    }
    if (step === 'stakes') {
      setError(null);
      if (needsTeams) setStep('teams');
      else void save();
    }
  };

  const goBack = () => {
    setError(null);
    if (step === 'teams') {
      setStep(newIds.length ? 'stakes' : 'pick');
      return;
    }
    if (step === 'stakes') setStep('pick');
    else onClose();
  };

  const title = step === 'pick' ? 'Add Games' : step === 'stakes' ? 'Stakes & Scoring' : 'Teams';

  return (
    <Sheet title={title} onClose={onClose}>
      <div className="space-y-4 px-4 pb-8 pt-2">
        <button type="button" onClick={goBack} className="inline-flex items-center gap-1 text-sm font-semibold text-ink-soft">
          <ChevronLeft size={18} /> Back
        </button>

        {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        {step === 'pick' && (
          <>
            <div className="rounded-xl border border-gold/30 bg-gold/10 px-3.5 py-3">
              <div className="text-xs font-bold text-navy">Select one or more games</div>
              <p className="mt-1 text-[11px] leading-relaxed text-ink-soft">
                Games are grouped by type. You can run several at once on this scorecard.
              </p>
            </div>
            <CategorizedGamePicker
              enabled={enabled}
              onToggle={toggleGame}
              playerCount={playerCount}
              onRound={Object.fromEntries([...existingIds].map((id) => [id, true]))}
            />
          </>
        )}

        {step === 'stakes' && (
          <>
            <StakesScoringPanel
              enabledIds={newIds}
              stakes={stakes}
              onChange={setStakes}
              roster={stakesRoster}
              organizerKey={orgKey}
              opponentKeys={matchOpponents}
              onOpponentChange={(formatId, opponentKey) => {
                setMatchOpponents((prev) => ({ ...prev, [formatId]: opponentKey }));
              }}
              nassauMatches={nassauMatches}
              onNassauMatchesChange={(formatId, matches) => {
                setNassauMatches((prev) => ({ ...prev, [formatId]: matches }));
              }}
              teams={previewTeams}
            />
            {(enabled.wolf || enabled['lone-wolf']) && (
              <WolfSetupPanel
                config={wolfConfig}
                onChange={setWolfConfig}
                showPointMultipliers={wolfShowMult}
              />
            )}
          </>
        )}

        {step === 'teams' && (
          <RoundTeamsSetupPanel
            roster={roster}
            teamMode={teamMode}
            onTeamModeChange={setTeamMode}
            manualTeams={manualTeams}
            onManualTeamChange={(playerId, teamIndex) => {
              setManualTeams((prev) => ({ ...prev, [playerId]: teamIndex }));
            }}
          />
        )}

        <div className="flex gap-2">
          {step === 'teams' ? (
            <button
              type="button"
              disabled={submitting}
              onClick={() => void save()}
              className="flex-1 rounded-xl bg-gold py-3 text-sm font-extrabold text-navy disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Save Games'}
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              className="flex-1 rounded-xl bg-navy py-3 text-sm font-bold text-cream"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}
